import { useNavigate } from 'react-router-dom';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import TableStyle from '../../ui-component/TableStyle';
import { useState } from 'react';
import {
  Stack,
  Button,
  Container,
  Typography,
  Box,
  Card,
  Paper,
  Grid,
  Chip,
  alpha,
  useTheme,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress
} from '@mui/material';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { getApi } from 'views/services/api';
import moment from 'moment';
import {
  Hotel as HotelIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as ActiveIcon,
  Cancel as ExpiredIcon,
  Warning as WarningSoonIcon,
  CardMembership as PackageIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as BankIcon,
  CreditCard as StripeIcon,
  CurrencyBitcoin as BinanceIcon,
  AccountBalanceWallet as ZelleIcon,
  PhoneIphone as PagoMovilIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';

// ----------------------------------------------------------------------

const METHOD_META = {
  stripe: { label: 'Stripe', Icon: StripeIcon, color: '#635BFF' },
  binance: { label: 'Binance', Icon: BinanceIcon, color: '#F0B90B' },
  zelle: { label: 'Zelle', Icon: ZelleIcon, color: '#6D1ED4' },
  pago_movil: { label: 'Pago Móvil', Icon: PagoMovilIcon, color: '#26A69A' },
  bank_transfer: { label: 'Transferencia', Icon: BankIcon, color: '#1E88E5' }
};

const AdminPayments = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [paymentData, setpaymentData] = useState([]);
  const [proofDialog, setProofDialog] = useState({ open: false, row: null });

  const fetchpaymentData = async () => {
    try {
      const response = await getApi(`api/payments/totalPayment`);
      setpaymentData(response?.data?.paymentsData || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchpaymentData();
  }, []);

  const getStatusSpanish = (status) => {
    switch (status) {
      case 'active':
        return 'Activo';
      case 'expired':
        return 'Vencido';
      case 'expiresSoon':
        return 'Por Vencer';
      default:
        return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <ActiveIcon sx={{ fontSize: 16 }} />;
      case 'expired':
        return <ExpiredIcon sx={{ fontSize: 16 }} />;
      case 'expiresSoon':
        return <WarningSoonIcon sx={{ fontSize: 16 }} />;
      default:
        return <PaymentIcon sx={{ fontSize: 16 }} />;
    }
  };

  // ---- Estadísticas ----
  const totalPayments = paymentData?.length || 0;
  const totalRevenue = paymentData?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
  const activePayments = paymentData?.filter((p) => p.status === 'active')?.length || 0;
  const uniqueHotels = [...new Set(paymentData?.map((p) => p.hotelName))].length || 0;

  const methodDistribution = useMemo(() => {
    const counts = {};
    (paymentData || []).forEach((p) => {
      const m = p.paymentMethod || 'stripe';
      counts[m] = (counts[m] || 0) + 1;
    });
    const total = paymentData?.length || 1;
    return Object.entries(counts)
      .map(([code, count]) => ({
        code,
        count,
        pct: Math.round((count / total) * 100)
      }))
      .sort((a, b) => b.count - a.count);
  }, [paymentData]);

  const topMethod = methodDistribution[0];

  const handleOpenProof = (row) => setProofDialog({ open: true, row });
  const handleCloseProof = () => setProofDialog({ open: false, row: null });

  const columns = [
    {
      field: 'hotelName',
      headerName: 'Hotel',
      flex: 1.4,
      cellClassName: 'name-column--cell name-column--cell--capitalize',
      renderCell: (params) => (
        <Box
          onClick={() => navigate(`/dashboard/hotel/view/${params.row.hotelId}`)}
          sx={{ cursor: 'pointer' }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontSize: '0.75rem',
                fontWeight: 600
              }}
            >
              {params.value?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                '&:hover': { color: theme.palette.primary.main, textDecoration: 'underline' }
              }}
            >
              {params.value}
            </Typography>
          </Stack>
        </Box>
      )
    },
    {
      field: 'packageName',
      headerName: 'Paquete',
      flex: 1.1,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <PackageIcon sx={{ fontSize: 16, color: theme.palette.secondary.main }} />
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        </Stack>
      )
    },
    {
      field: 'paymentMethod',
      headerName: 'Método',
      flex: 1,
      renderCell: (params) => {
        const meta = METHOD_META[params.value || 'stripe'] || METHOD_META.stripe;
        const Icon = meta.Icon;
        return (
          <Chip
            size="small"
            icon={<Icon sx={{ fontSize: 16 }} />}
            label={meta.label}
            sx={{
              bgcolor: alpha(meta.color, 0.12),
              color: meta.color,
              fontWeight: 600,
              borderRadius: 2,
              '& .MuiChip-icon': { color: meta.color }
            }}
          />
        );
      }
    },
    {
      field: 'amount',
      headerName: 'Monto',
      flex: 0.8,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <MoneyIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
          <Typography variant="body2" fontWeight={600} color="success.main">
            ${params.value?.toLocaleString()}
          </Typography>
        </Stack>
      )
    },
    {
      field: 'confirmationNumber',
      headerName: 'N° Confirmación',
      flex: 1.2,
      renderCell: (params) => {
        const value = params.value;
        if (!value)
          return (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          );
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                maxWidth: 140,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={value}
            >
              {value}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                navigator.clipboard?.writeText(value);
              }}
              title="Copiar"
            >
              <CopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        );
      }
    },
    {
      field: 'paymentProof',
      headerName: 'Comprobante',
      flex: 0.9,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const url = params.row?.paymentProof?.url;
        if (!url)
          return (
            <Typography variant="caption" color="text.disabled">
              N/A
            </Typography>
          );
        return (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewIcon />}
            onClick={() => handleOpenProof(params.row)}
          >
            Ver
          </Button>
        );
      }
    },
    {
      field: 'startDate',
      headerName: 'Fecha',
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <CalendarIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {moment(params.value).format('DD/MM/YYYY')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {moment(params.value).format('HH:mm')}
            </Typography>
          </Box>
        </Stack>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      flex: 0.9,
      renderCell: (params) => {
        let chipColor;
        switch (params.value) {
          case 'active':
            chipColor = 'success';
            break;
          case 'expired':
            chipColor = 'error';
            break;
          case 'expiresSoon':
            chipColor = 'warning';
            break;
          default:
            chipColor = 'default';
        }
        return (
          <Chip
            label={getStatusSpanish(params.value)}
            color={chipColor}
            variant="filled"
            size="small"
            icon={getStatusIcon(params.value)}
            sx={{ borderRadius: 2, fontWeight: 600, fontSize: '0.75rem', minWidth: 100 }}
          />
        );
      }
    }
  ];

  const proofRow = proofDialog.row;
  const proofUrl = proofRow?.paymentProof?.url;
  const isImage = proofRow?.paymentProof?.mimeType?.startsWith('image/');

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 3
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Administración de Pagos Globales
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Panel centralizado para monitorear todos los pagos del sistema
              </Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <BankIcon sx={{ fontSize: 60, opacity: 0.3 }} />
            </Box>
          </Stack>
        </Paper>

        {/* Stat cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PaymentIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="primary">
                    {totalPayments}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Pagos
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="success.main">
                    ${totalRevenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ingresos Totales
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ActiveIcon sx={{ color: theme.palette.info.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="info.main">
                    {activePayments}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Suscripciones Activas
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <HotelIcon sx={{ color: theme.palette.warning.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="warning.main">
                    {uniqueHotels}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hoteles Suscriptores
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Distribución de métodos de pago */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Distribución por Método de Pago
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {topMethod
                  ? `El método más usado es ${METHOD_META[topMethod.code]?.label || topMethod.code} (${topMethod.pct}%)`
                  : 'Aún no hay datos suficientes'}
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={1.5}>
            {methodDistribution.map((m) => {
              const meta = METHOD_META[m.code] || { label: m.code, color: theme.palette.grey[500], Icon: PaymentIcon };
              const Icon = meta.Icon;
              return (
                <Box key={m.code}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Icon sx={{ fontSize: 18, color: meta.color }} />
                      <Typography variant="body2" fontWeight={600}>
                        {meta.label}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {m.count} pago{m.count !== 1 ? 's' : ''} · {m.pct}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={m.pct}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(meta.color, 0.12),
                      '& .MuiLinearProgress-bar': { bgcolor: meta.color }
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        </Card>

        {/* Tabla */}
        <TableStyle>
          <Card
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Box sx={{ p: 3, pb: 1 }}>
              <Typography variant="h6" fontWeight={600} color="primary">
                Registro Global de Pagos ({totalPayments})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Haz clic en un hotel para ver sus detalles, o en &quot;Ver&quot; para revisar el comprobante
              </Typography>
            </Box>
            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={paymentData}
                columns={columns}
                checkboxSelection
                getRowId={(row) => row?._id}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: {
                      debounceMs: 500,
                      placeholder: 'Buscar por hotel, paquete, método...'
                    }
                  }
                }}
              />
            </Box>
          </Card>
        </TableStyle>
      </Container>

      {/* Proof viewer dialog */}
      <Dialog open={proofDialog.open} onClose={handleCloseProof} maxWidth="md" fullWidth>
        <DialogTitle>
          Comprobante de pago
          <IconButton onClick={handleCloseProof} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {proofRow && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip label={`Hotel: ${proofRow.hotelName}`} />
                <Chip label={`Plan: ${proofRow.packageName}`} />
                <Chip label={`Monto: $${proofRow.amount}`} color="success" />
                <Chip
                  label={`Método: ${METHOD_META[proofRow.paymentMethod]?.label || proofRow.paymentMethod}`}
                  color="primary"
                />
              </Stack>
              {proofRow.confirmationNumber && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    N° de confirmación / referencia
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                  >
                    {proofRow.confirmationNumber}
                  </Typography>
                </Box>
              )}
              {proofUrl ? (
                isImage ? (
                  <Box
                    component="img"
                    src={proofUrl}
                    alt="Comprobante"
                    sx={{ width: '100%', borderRadius: 2, maxHeight: 600, objectFit: 'contain' }}
                  />
                ) : (
                  <Box
                    component="iframe"
                    src={proofUrl}
                    title="Comprobante PDF"
                    sx={{ width: '100%', height: 600, border: 0, borderRadius: 2 }}
                  />
                )
              ) : (
                <Typography color="text.secondary">No hay comprobante adjunto.</Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {proofUrl && (
            <Button href={proofUrl} target="_blank" rel="noreferrer" variant="contained">
              Abrir en nueva pestaña
            </Button>
          )}
          <Button onClick={handleCloseProof}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminPayments;
